/**
 * Knowledge retrieval — the read side of `knowledge_chunk` (ADR-0022 D4).
 *
 * These types exist to make the scope predicate's THREE states per axis
 * representable, because two-state thinking is what breaks it: an axis the
 * request does not constrain must emit **no clause at all**, which is a
 * different thing from "match rows that apply to all values".
 */

import { type AxisKind, ClassifiedObjectType } from '../classification/classification.enums.js'
import type { BlockLocation } from '../extraction/extraction.types.js'
import type { ChunkKind, UnindexedReason } from './knowledge.enums.js'
import type { ObjectScopeTuple } from './knowledge-indexing.types.js'

/**
 * The object types the knowledge base can index, and therefore compare.
 *
 * `ClassifiedObjectType` has nine members; only these two have somewhere for their
 * text to come from. Declared once here because three places need the same answer —
 * the indexer's source registry, the citation registry, and the input validation for
 * a comparison — and a fourth list would be a fourth thing to drift. A parity test
 * asserts all three against this one.
 *
 * Adding a type means registering a text source and a citation source, then adding
 * it here.
 */
export const INDEXABLE_OBJECT_TYPES = [ClassifiedObjectType.FILE, ClassifiedObjectType.ARTICLE] as const

/** An object type the knowledge base can index. See {@link INDEXABLE_OBJECT_TYPES}. */
export type IndexableObjectType = (typeof INDEXABLE_OBJECT_TYPES)[number]

/**
 * One axis the request DOES constrain, with its values already expanded to
 * themselves-plus-ancestors through `axis_value_closure`.
 *
 * Expansion is on the request side and in the ancestor direction only: a
 * `global` chunk matches a `latam_mx` request, a `latam_mx` chunk does not
 * match a `latam` request.
 */
export interface AxisConstraint {
	axisCode: string
	/** Never empty — an axis constrained to no values constrains nothing, so it is dropped instead. */
	valueCodes: string[]
}

/**
 * The scope predicate as data, one step before it becomes SQL: every axis the
 * request constrains, and nothing else.
 *
 * An axis missing from this list is the first of the three states — *the request
 * does not constrain it* — and must produce no clause. The other two states are
 * both inside a present `AxisConstraint`: the assertion's value is in
 * `valueCodes`, or the assertion does not constrain the axis at all and
 * therefore applies to every value of it.
 *
 * Every axis is treated identically. There are no privileged axes with their own
 * columns, because which axes matter is tenant data — promoting one must never be
 * a migration.
 */
export type ScopeFilter = AxisConstraint[]

/**
 * Citation metadata, hydrated from the live source row on every query — never
 * copied onto the chunk. A file renamed after indexing therefore cites
 * correctly with no re-index, and a soft-deleted source yields no citation at
 * all, which drops the passage (ADR-0022 D4, deletion layer 3).
 */
export interface IKnowledgeCitation {
	objectType: ClassifiedObjectType
	objectId: string
	/** Human-facing label of the source — a file's current `file_name`. */
	title: string
	contentType: string | null
}

/** One ranked, in-scope, cited passage. The rank itself is deliberately absent. */
export interface IKnowledgePassage {
	chunkId: string
	objectType: ClassifiedObjectType
	objectId: string
	chunkKind: ChunkKind
	chunkIndex: number
	contentText: string
	contextPrefix: string | null
	// Note: the matched assertion is deliberately NOT reported. Naming it costs
	// a lateral join on every query to answer a question `/matching/explain`
	// already answers for a single object, on demand.
	/** Rendering + provenance (which extractor produced the text). Omitted from the DTO. */
	meta: Record<string, unknown>
	/**
	 * Where in the source this passage begins — the opaque typed pointer, so a
	 * reviewer can find the text in the original.
	 *
	 * A discriminated pointer rather than a `page` number, because a transcript's
	 * position is a point in TIME and a client that reads `passage.page` has baked
	 * "sources are paginated" into its rendering. Read `kind` before `value` means
	 * anything; never do arithmetic on the value.
	 *
	 * Lifted OUT of `meta` for the wire, unlike the rest of it: `meta` is an
	 * operator's record and stays server-side, where this is the one part of it a
	 * reader legitimately needs in order to act on a citation.
	 */
	location: BlockLocation | null
	citation: IKnowledgeCitation
	createdAt: Date
}

/**
 * The caller-facing filter, shared by every read method.
 *
 * Deliberately ONE definition rather than a shape per method: sharing it is what
 * makes "an object reachable by `ask` is listed by `browse` under the same
 * constraints" true by construction rather than by two implementations agreeing.
 *
 * Distinct from {@link ScopeFilter}, which is the internal predicate-as-data one
 * step from SQL. This is what a caller supplies; the service resolves it INTO a
 * `ScopeFilter` (values expanded to ancestors) plus the row-level clauses below.
 *
 * Every field here is admissible under one rule: it must translate into a `WHERE`,
 * `ORDER BY` or `LIMIT` clause of the single statement that reads. Post-filtering
 * in application code is excluded — it moves the compliance rule somewhere a
 * reviewer cannot see by reading the query, and it silently breaks `limit`.
 */
export interface KnowledgeFilter {
	/** Flat axis map — dimensions and attributes alike. Absent or empty constrains nothing. */
	scope?: Partial<Record<string, string[]>>
	objectTypes?: ClassifiedObjectType[]
	objectIds?: string[]
	chunkKinds?: ChunkKind[]
	/** Chunks indexed at or after this instant. */
	indexedSince?: Date
	exclude?: KnowledgeExclusion
}

/**
 * The negative half of a filter — what a result must NOT carry.
 *
 * Negation is offered because it is the one constraint a question cannot express:
 * a phrasing drops or inverts "not restricted", and a ranking cannot enforce it at
 * all. Its semantics are four decisions, all of them load-bearing:
 *
 * - **Object-scoped.** An object carrying an excluded value on ANY assertion is
 *   out, not only when the assertion satisfying the positive half carries it.
 *   Excluding too much loses recall, which is visible; excluding too little hands
 *   back exactly what the caller said to withhold.
 * - **Downward-expanding.** An excluded value covers itself and its DESCENDANTS —
 *   the mirror of the positive side's ancestor expansion — so excluding `emea`
 *   excludes `emea_es`. Never upward: that would remove all `global` material.
 * - **Anti-joined**, never key-negated. See `notInScopeCondition`.
 * - **Absence-tolerant.** An object with no value on the excluded axis SURVIVES.
 *   So an exclusion removes what is known to carry a value; it is not proof of
 *   safety. Requiring a known-good value is a POSITIVE constraint naming the
 *   acceptable values, which the closed-world positive rule already keeps
 *   untagged objects out of.
 */
export interface KnowledgeExclusion {
	scope?: Partial<Record<string, string[]>>
	chunkKinds?: ChunkKind[]
	objectIds?: string[]
}

/**
 * One browsed object — a document, not a passage.
 *
 * Carries the same `citation` type a passage does, so a browse row and an ask
 * result render through one component and the "never surface content without a
 * citation" rule holds identically on both paths.
 */
export interface IKnowledgeObjectSummary {
	objectType: ClassifiedObjectType
	objectId: string
	citation: IKnowledgeCitation
	liveChunkCount: number
	lastIndexedAt: Date
}

/**
 * One page of a keyset-paginated listing.
 *
 * `nextCursor` is `null` on the last page, and there is deliberately no separate
 * `hasMore`: one signal, nothing to drift.
 */
export interface KnowledgePage<T> {
	items: T[]
	nextCursor: string | null
	/**
	 * Distinct in-scope OBJECTS the filter admits, only when `includeTotal` was
	 * requested — never computed otherwise.
	 *
	 * The unit is objects at BOTH grains. At passage grain this is deliberately not
	 * the number of `items`: one document's 120 chunks listed 50 at a time report
	 * `total: 1`, because the question the flag answers is "how many documents match",
	 * which stays meaningful beside a passage read.
	 */
	total?: number
	/** Only when `includeFacets` was requested — never computed otherwise. */
	facets?: KnowledgeVocabulary
}

/**
 * What a caller may filter by, as observed under the filter it already supplied.
 *
 * The vocabulary is not deferrable to a hardcoded rail: a consumer that cannot
 * hardcode one — a tool surface above all — has no other way to learn that
 * `brand=bravecto` exists, and the counts are what tell it whether narrowing
 * further is worth doing or about to return nothing.
 */
export interface KnowledgeVocabulary {
	objectTypes: ClassifiedObjectType[]
	chunkKinds: ChunkKind[]
	axes: KnowledgeAxisFacet[]
}

/** One axis as the registry describes it, with the values actually observed on it. */
export interface KnowledgeAxisFacet {
	code: string
	kind: AxisKind
	isHierarchical: boolean
	values: KnowledgeAxisValueFacet[]
}

export interface KnowledgeAxisValueFacet {
	code: string
	/**
	 * The registry label, or `null` for a value with no `axis_value` row.
	 *
	 * Nullable because a free-text attribute axis has no value table to label from —
	 * the code IS the value a user typed — and reporting the code as its own label
	 * would make an unlabelled value indistinguishable from a labelled one.
	 */
	label: string | null
	/** Distinct in-scope objects carrying this value under the supplied filter. */
	objectCount: number
}

/**
 * One object's index state — the diagnostic half of the query surface.
 *
 * Structurally NOT a point in the browse grammar, which is why it is its own
 * method: for an unindexed object a listing's answer is absence, and absence
 * cannot carry a reason. Every count here is computed with retrieval's OWN
 * eligibility predicate, so a counted chunk is one retrieval would consider — but
 * the counts carry no scope filter and no citation gate, so they report the
 * orphaned rows a listing correctly hides rather than matching it exactly.
 */
export interface IKnowledgeIndexState {
	objectType: ClassifiedObjectType
	objectId: string
	indexed: boolean
	unindexedReason: UnindexedReason | null
	/** The projection as stored — one row per assertion, the object's attributes merged into each. */
	scope: ObjectScopeTuple[]
	liveChunkCount: number
	embeddedChunkCount: number
	/**
	 * Distinct embedding models across the live chunks. An array rather than a
	 * single value because a length above one is the observable symptom of a
	 * half-migrated object, and the dense arm ranks only within one model's space.
	 */
	models: string[]
	lastIndexedAt: Date | null
}

/**
 * The chunk-level half of a filter, one step from SQL — the fields that are plain
 * column predicates on `knowledge_chunk` rather than a test against the object's
 * projection.
 *
 * Kept apart from {@link ScopeFilter} because the two are enforced differently and
 * a reader must be able to see which is which: the scope halves are correlated
 * subqueries over `knowledge_object_scope` and carry the compliance guarantee,
 * these are ordinary columns on the row being returned.
 */
export interface ChunkRowFilter {
	objectTypes: ClassifiedObjectType[]
	objectIds: string[]
	chunkKinds: ChunkKind[]
	indexedSince?: Date
	excludedChunkKinds: ChunkKind[]
	excludedObjectIds: string[]
}

/**
 * A {@link KnowledgeFilter} with its axis values resolved against the registry —
 * the positive side expanded to ancestors, the excluded side to descendants — and
 * its row-level fields separated out.
 *
 * Every read method takes exactly this, which is the mechanism behind "browse and
 * ask cannot disagree": there is one resolution step and one clause composer, so a
 * constraint enforced on one path cannot be missing on another.
 */
export interface ResolvedKnowledgeFilter {
	scope: ScopeFilter
	/** Empty means "exclude nothing" and MUST emit no clause — see `notInScopeCondition`. */
	excludedScope: ScopeFilter
	rows: ChunkRowFilter
}

/**
 * One paragraph of the probe document, as the comparison used it.
 *
 * `index` is the probe's position in the SAMPLE, not in the document, and the
 * distinction matters because a long document is strided: probe 3 of 30 may be the
 * document's 87th paragraph. `chunkIndex` carries the document position for a
 * caller that wants to point at the original.
 *
 * `text` is present so a side-by-side view can render both halves of a pair without
 * a second round trip to the probe. It is the probe's own text — the caller's
 * document — so returning it discloses nothing the caller did not supply, which is
 * why it is here and not gated.
 */
export interface IProbeParagraph {
	index: number
	chunkIndex: number
	text: string
	location: BlockLocation | null
}

/**
 * One matched library passage, with the probe paragraphs that reached it.
 *
 * It composes {@link IKnowledgePassage} rather than extending it, so the passage
 * half stays byte-identical to what `ask` and `browse` return and one renderer
 * serves all three. `matchedProbeIndexes` is the alignment — the whole reason this
 * grain exists rather than being a filtered `ask`.
 *
 * There is deliberately **no distance**. ADR-0022's rule that a rank never leaves
 * the database is not weaker here just because the query is a document: a
 * publishable distance invites a client to threshold on a number that belongs to
 * one model on one corpus.
 */
export interface IKnowledgeAlignment {
	passage: IKnowledgePassage
	/** Ascending, distinct. Never empty — a passage with no matching probe is not a row. */
	matchedProbeIndexes: number[]
}

/**
 * One candidate document, with how much of the probe it accounts for.
 *
 * `matchedProbeCount` over the result's `probeCount` is COVERAGE, and it is
 * deliberately given as two integers rather than a ratio: a caller that wants a
 * percentage can divide, while a caller shown "12 of 18" can see that the denominator
 * is a sample of a longer document. A pre-divided float hides that.
 */
export interface IKnowledgeSimilarObject {
	objectType: ClassifiedObjectType
	objectId: string
	citation: IKnowledgeCitation
	/** Distinct probes with at least one match in this object. */
	matchedProbeCount: number
	/**
	 * Distinct chunks of this object that matched at least one probe.
	 *
	 * **Not the object's chunk count**, and deliberately not named `liveChunkCount`,
	 * which means exactly that everywhere else it appears. The population here is
	 * already narrowed by the model filter and the distance ceiling, so a 412-chunk
	 * guideline with five matching paragraphs reports five. Reading it as a size would
	 * turn a coverage figure of 5/412 into 5/5.
	 */
	matchedChunkCount: number
	lastIndexedAt: Date
}

/**
 * A comparison's result — the items, plus what the probe actually was.
 *
 * The probe report is part of the ANSWER, not diagnostics: `probeCount` is the
 * denominator every coverage figure is expressed against, and `truncated` is the
 * difference between "your document is 30% covered" and "the 30 paragraphs we
 * sampled are 30% covered". Omitting either would let a caller state a stronger
 * conclusion than the data supports.
 */
export interface KnowledgeSimilarResult<T> {
	items: T[]
	/** How many probe paragraphs were embedded and matched — the coverage denominator. */
	probeCount: number
	/** True when the probe document yielded more chunks than the cap, so probes were sampled. */
	truncated: boolean
	/** The probe paragraphs themselves, in probe order, for rendering the other half of a pair. */
	probes: IProbeParagraph[]
}
